// Titles and descriptions for every route, in one place.
//
// These are needed twice: the components pass them to <Seo> so the browser tab
// and the tags Google's renderer sees are right, and scripts/prerender.mjs
// writes them into static HTML so social crawlers, which run no JavaScript, see
// them too. They used to be typed out separately in both places, and had
// already drifted - the prerendered pages said one thing and the rendered page
// overwrote it with another. Google runs JavaScript, so the client-side copy is
// the one that wins, which is the worst way for the two to disagree.
//
// On the format: keyword first, brand last. Nobody searches for "d2index", so
// "d2index | Charts" spent the most valuable part of the title on a term with no
// search volume and said almost nothing about the page. <Seo> and the prerender
// script both append " | d2index", so titles here leave it off.
//
// Descriptions are what shows under the link in search results. Aim for 150-160
// characters: much shorter wastes the space, much longer is truncated.

// `heading` and `intro` are the page's own visible copy, not metadata. They are
// here because the prerender step writes them into the HTML body so a crawler
// arriving without JavaScript finds something to read - every page used to ship
// nine words, all of them "You need to enable JavaScript to run this app."
//
// That only works if the component renders the same words. Prerendering text the
// page does not actually show is cloaking, so these are the single source for
// both, exactly as title and description are.
export const routeMeta = {
  "/": {
    heading: "Bitcoin Live",
    intro:
      "A live feed of global, unconfirmed Bitcoin transactions as they're submitted.",
    title: "Bitcoin Live — Watch Transactions in Real Time",
    description:
      "Watch unconfirmed Bitcoin transactions stream in live, see each block land and what it confirmed, and spot transfers from exchange and government wallets.",
    // The feed, the panels and the chart are all live data, so without these the
    // served page is a heading and one sentence - the front page, and the one
    // meant to answer "watch live bitcoin transactions", had the least text on
    // the site. These explain what the numbers on it mean, which is true however
    // old the build is.
    faq: [
      {
        question: "What is an unconfirmed Bitcoin transaction?",
        answer:
          "One that has been broadcast to the network but not yet included in a block. This page shows them as they are relayed and marks each one confirmed when a block containing it arrives, about every ten minutes. Until then it can still be replaced by a version paying a higher fee, or dropped.",
      },
      {
        question: "Why is the amount moved smaller than the transaction's outputs?",
        answer:
          "A transaction spends earlier outputs whole, so whatever the sender does not pay away comes back to them as another output, called change. Adding up every output counts that change as though it moved. The figures here are net: an output returning to an address that funded the transaction is left out, and so is the change in a recognisable batch payout.",
      },
      {
        question: "How are exchange and government wallets recognised?",
        answer:
          "By matching every transaction against a short, hand-checked list of notable addresses: exchange cold and hot wallets, government seizures, historic addresses and large unattributed holdings. A match is highlighted, and when one side is an exchange the whale panel says whether coins moved onto it or off it. Most exchange traffic passes through deposit addresses used once, so most of it is never flagged, and every label is an attribution rather than proof of ownership.",
      },
    ],
  },
  "/mempool": {
    heading: "Mempool & Fees",
    intro:
      "What it costs to get a Bitcoin transaction confirmed right now, and how much is queued up waiting.",
    title: "Bitcoin Fees & Mempool Right Now",
    description:
      "What a Bitcoin transaction costs right now, in sat/vB and in dollars, with the current mempool backlog and the queue of blocks waiting to be mined.",
    // Answers that do not depend on live figures, so they can be written into
    // the served HTML rather than only appearing once React has run.
    //
    // The page asks five questions; the other two quote the current fee and the
    // current backlog, which a build cannot know. These three are the ones worth
    // having in the HTML: they are what somebody who does not know what a
    // mempool is needs, and what a crawler that runs no JavaScript can read.
    faq: [
      {
        question: "What is the mempool?",
        answer:
          "Every transaction that has been broadcast but not yet included in a block. Miners pick from it, highest fee rate first, so the mempool is both the waiting room and the auction.",
      },
      {
        question: "Why are fees measured in sat/vB rather than dollars?",
        answer:
          "Because miners are selling space, not value. A transaction's cost depends on how many bytes it occupies, so moving 0.001 BTC and moving 1,000 BTC cost the same if they are the same shape. Consolidating many small inputs is expensive for exactly this reason.",
      },
      {
        question: "Can a transaction be stuck forever?",
        answer:
          "It can sit for a long time. Nodes drop transactions from their mempool after a couple of weeks, at which point it is as if it was never sent. Wallets that support replace-by-fee can rebroadcast the same transaction with a higher fee instead of waiting.",
      },
    ],
  },
  "/charts": {
    heading: "Price Charts",
    intro:
      "Bitcoin, Ethereum and the major cryptocurrencies over the last day, week, month or year. Pick a coin and a timeframe.",
    title: "Bitcoin & Crypto Price Charts",
    description:
      "Price charts for Bitcoin, Ethereum and the major cryptocurrencies over the last day, week, month or year, with the current price and change.",
  },
  "/watchlist": {
    heading: "Watchlist",
    intro:
      "The top cryptocurrencies ranked by the volume actually traded over the last 24 hours, with price, change and market cap.",
    title: "Top Crypto Prices by Volume",
    description:
      "Live prices, 24 hour change, trading volume and market cap for the top cryptocurrencies, ranked by the volume actually traded.",
  },
  "/defi": {
    heading: "Bitcoin in DeFi",
    intro:
      "How much Bitcoin is locked up backing wrapped and staked versions of it on other chains, and how big Bitcoin's own DeFi layer actually is.",
    title: "How Much Bitcoin Is Locked in DeFi",
    description:
      "The Bitcoin held in custody behind WBTC, tBTC, LBTC and the rest, counted in BTC rather than dollars, with the total value locked on Bitcoin sidechains.",
    // Both tables are live figures, so without these the served page is a
    // heading and one sentence. These answer what the numbers mean, which is
    // true however old the build is - and the double-counting question is the
    // one a reader who knows the subject will ask first.
    faq: [
      {
        question: "What does it mean for Bitcoin to be locked in DeFi?",
        answer:
          "Bitcoin cannot move to another blockchain, so a protocol takes custody of real BTC and issues a token elsewhere that stands for it. WBTC on Ethereum is the largest example. The Bitcoin does not go anywhere - it sits in custody addresses, and the token is what circulates. Redeeming the token is what releases the coins again.",
      },
      {
        question: "Why count in BTC rather than dollars?",
        answer:
          "Because the quantity of Bitcoin held is the fact, and the dollar figure is a translation of it that changes when the price does. A custody balance can be flat for a month while its dollar value moves twenty percent. Counting coins separates what the protocols did from what the market did.",
      },
      {
        question: "Is the same Bitcoin counted twice?",
        answer:
          "No. Only Bitcoin held on the Bitcoin chain is counted. Several protocols hold wrapped Bitcoin on other chains - a staking protocol holding WBTC on Ethereum, for instance - and that Bitcoin is already counted once, in the custody balance of the wrapper that issued it. Counting it again in both places would inflate the total.",
      },
      {
        question: "Does this prove the wrapped tokens are fully backed?",
        answer:
          "It does not. These are the balances the protocols report, as measured by DefiLlama. A real proof of reserve means checking the custody addresses on the Bitcoin chain and comparing them to the supply of the token issued against them. That check is not done here, and the figures should be read as what is claimed rather than what is verified.",
      },
    ],
  },
  // Category pages. These exist for search as much as for browsing: "binance
  // cold wallet address" and "bitcoin mining pool addresses" are things people
  // look for, and a page of hand-written notes answers them where a live tape
  // cannot. They are also the site's only pages genuinely made of words.
  "/btc-explorer/exchanges": {
    heading: "Exchange Bitcoin Wallets",
    intro:
      "Cold and hot wallets belonging to major exchanges, including the largest known Bitcoin address. Movements from these wallets are flagged on Bitcoin Live.",
    title: "Exchange Bitcoin Wallet Addresses",
    description:
      "Known Bitcoin cold and hot wallet addresses for Binance, Bitfinex and other major exchanges, with balances and what each one is used for.",
  },
  "/btc-explorer/government": {
    heading: "Government-Held Bitcoin",
    intro:
      "Addresses holding bitcoin seized by governments. Balances tend to sit untouched for years and then move all at once.",
    title: "Government Bitcoin Seizure Addresses",
    description:
      "Bitcoin addresses holding coins seized by governments, including the US Government's Silk Road forfeiture, with balances and the case behind each.",
  },
  "/btc-explorer/historic": {
    heading: "Historic Bitcoin Addresses",
    intro:
      "Addresses that matter because of what happened at them rather than what they hold — the genesis block among them.",
    title: "Historic Bitcoin Addresses",
    description:
      "Bitcoin addresses of historic significance, including the genesis block coinbase output, with what each one is and why it matters.",
  },
  "/btc-explorer/whales": {
    heading: "Bitcoin Whale Wallets",
    intro:
      "Very large holdings with no confident public attribution. Big enough that a single movement is news.",
    title: "Bitcoin Whale Wallet Addresses",
    description:
      "The largest unattributed Bitcoin addresses, with balances and what is known about each — holdings big enough that one transfer moves the market.",
  },
  "/btc-explorer/mining-pools": {
    heading: "Mining Pool Payout Addresses",
    intro:
      "The address each mining pool pays its block rewards to, worked out from the chain rather than from anyone's say-so.",
    title: "Bitcoin Mining Pool Payout Addresses",
    description:
      "The Bitcoin address each major mining pool receives block rewards at — Foundry USA, AntPool, F2Pool and others — derived from the coinbase output of blocks they mined.",
  },
  // Sub-pages of the explorer are lists of addresses worth looking at. Keep
  // their slugs lowercase: they are prerendered as directories, and Netlify's
  // pretty-URL redirect lowercases the path on the way, which is what broke the
  // curated wallet pages when those were files. A lowercase slug has nothing to
  // lose to it.
  "/btc-explorer/sanctioned": {
    heading: "OFAC Sanctioned Bitcoin Addresses",
    intro:
      "Every Bitcoin address on the US Treasury's Specially Designated Nationals list, grouped by the party it was designated against.",
    title: "OFAC Sanctioned Bitcoin Addresses",
    description:
      "The full list of Bitcoin addresses sanctioned by the US Treasury, grouped by designated party, with the sanctions programs each falls under and a link to look up any of them on-chain.",
  },
  "/btc-explorer": {
    heading: "BTC Explorer",
    intro:
      "Look up any Bitcoin address or transaction hash, or browse notable wallets below: exchange cold storage, government seizures and known whales.",
    title: "Bitcoin Wallet & Transaction Explorer",
    description:
      "Look up any Bitcoin address or transaction hash and see its balance and history, or browse notable wallets: exchange cold storage, government seizures and known whales.",
  },
};

// The page for a URL that does not exist. Not part of routeMeta: it is not a
// route the site links to or puts in the sitemap, it is what Netlify serves with
// a 404 status - build/404.html for any unknown path, and for an explorer id
// that cannot be an address. src/NotFound.js renders these same words, so the
// served HTML and the page agree.
export const notFoundMeta = {
  heading: "Page not found",
  intro:
    "That URL does not exist. Bitcoin Live is the front page, and the explorer will look up any wallet address or transaction hash.",
  title: "Page not found",
  description: "That page does not exist on d2index.",
};

// The address list each curated category appears on, so a wallet page can link
// back to it.
const walletListRoutes = {
  Exchange: "/btc-explorer/exchanges",
  Government: "/btc-explorer/government",
  Whales: "/btc-explorer/whales",
  Historic: "/btc-explorer/historic",
};

// A curated wallet's own page. The label carries the search intent - people look
// for "Binance hot wallet address", not for the address itself - so it is the
// heading as well as the start of the title. The page used to head every one of
// these "Bitcoin Wallet Lookup" and title them "Binance — hot wallet Bitcoin
// Wallet", which said "wallet" twice and left the heading saying nothing.
//
// intro, provenance and caveat are what the page's card renders, in that order,
// because the edge function writes the same fields into the served HTML. That
// was a single sentence before: no address, no balance, no source and no way
// back to the list, which left a crawler reading it nothing to match.
export const walletMeta = (wallet, checked) => {
  const listRoute = walletListRoutes[wallet.category];
  const btc = wallet.approxBtc.toLocaleString();
  return {
    heading: wallet.label,
    intro: wallet.note,
    title: `${wallet.label}: Bitcoin Address and Balance`,
    description: `${wallet.label}, holding roughly ${btc} BTC. ${wallet.note}`,
    // Recorded, not live: the live balance loads with the page and is not known
    // when the HTML is served. The date keeps the two from being confused.
    provenance: {
      before: `Address ${wallet.address}, holding ~${btc} BTC when checked on ${checked}. Listed with the other `,
      link: { href: `${listRoute}/`, text: routeMeta[listRoute].heading },
      after: ".",
    },
    caveat: `An attribution, not proof of ownership. Source: ${wallet.source}.`,
  };
};

// Any other address or transaction hash. These are not prerendered - they are
// not known at build time - so this only reaches Google's renderer, not social
// crawlers, whose previews still fall back to the site-wide tags.
export const lookupMeta = (kind, id) => ({
  heading: `Bitcoin ${kind === "transaction" ? "Transaction" : "Wallet"} Lookup`,
  intro: id,
  title: `Bitcoin ${kind === "transaction" ? "Transaction" : "Wallet"} ${id.slice(0, 12)}…`,
  description: `Balance, history and transaction detail for Bitcoin ${kind} ${id}.`,
});
